import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { getErrorMessage } from './utils/errorHandler';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import DashboardGame from './pages/DashboardGame';
import Contacts from './pages/Contacts';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import Responses from './pages/Responses';
import CampaignResponses from './pages/CampaignResponses';
import Espera from './pages/Espera';
import WhatsApp from './pages/WhatsApp';
import Users from './pages/Users';
import Evolucao from './pages/Evolucao';
import Layout from './components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      toast.success('Login realizado com sucesso!');
      return true;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      return false;
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      toast.success('Conta criada com sucesso!');
      return true;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Logout realizado');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  // Protected Route Component
  const ProtectedRoute = ({ allowedRoles, children }) => {
    if (!user) return <Navigate to="/login" />;
    const userRole = user.role || 'user';
    if (!allowedRoles.includes(userRole)) {
      toast.error('Você não tem permissão para acessar esta página');
      return <Navigate to="/dashboard" />;
    }
    return children;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
          <Route path="/register" element={!user ? <Register /> : <Navigate to="/dashboard" />} />
          
          <Route element={user ? <Layout /> : <Navigate to="/login" />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard-game" element={<DashboardGame />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/evolucao" element={
              <ProtectedRoute allowedRoles={['admin', 'cognicao']}>
                <Evolucao />
              </ProtectedRoute>
            } />
            <Route path="/campaigns" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Campaigns />
              </ProtectedRoute>
            } />
            <Route path="/campaigns/:id" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <CampaignDetail />
              </ProtectedRoute>
            } />
            <Route path="/responses" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Responses />
              </ProtectedRoute>
            } />
            <Route path="/responses/:campaignId" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <CampaignResponses />
              </ProtectedRoute>
            } />
            <Route path="/espera" element={
              <ProtectedRoute allowedRoles={['admin', 'recepcao']}>
                <Espera />
              </ProtectedRoute>
            } />
            <Route path="/whatsapp" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <WhatsApp />
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Users />
              </ProtectedRoute>
            } />
          </Route>
          
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthContext.Provider>
  );
}

export default App;
